//REACT AND REACT BASED LIBRARIES
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
//COMPONENTS
import { Box, Container } from "@mui/material";
import AddEditFormCst from "@components/AddEditFormCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { useModuleActivityAddEdit } from "../hooks/useModuleActivityAdd";
import { PagesID } from "@utils/utilsConfigurations";
import { ServicesURL } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";

const ModuleEdit: React.FC = () => {
  const { t } = useTranslation(["manage-project-modules-page"]);
  const { id, IdPlan, IdProject, IdModule } = useParams();
  const navigate = useNavigate();

  const { columns, dataDetail, mutate } = useFetchDataDetail(
    `${ServicesURL.getModule
      .replace(":corporateId", id || "")
      .replace(":planId", IdPlan || "")
      .replace(":projectId", IdProject || "")
      .replace(":moduleId", IdModule || "")}`,
    PagesID["corporate.foundedTraining.managePlan.project.module.edit"],
  );

  const {
    activityAssociation,
    courseAssociation,
    pathAssociation,
    editionAssociation,
    openAssociations,
    showError,
    setOpenAssociation,
    handleAssociation,
    handleDelete,
    handleClearAssociation,
    inputsException,
    getValues,
    setActivityAssociation,
    setCourseAssociation,
    setPathAssociation,
    setEditionAssociation,
  } = useModuleActivityAddEdit(dataDetail);

  const columWithReset = columns?.map((el) => {
    if (el.accessorKey === "delivery_mode_type") {
      return { ...el, ...{ clearData: handleClearAssociation } };
    } else {
      return el;
    }
  });

  const [inputFormValidation, setInputFromValidation] = useState();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message?: string;
    description?: string;
    customContent?: JSX.Element;
  }>();

  const [openToastConfirm, setOpenToastConfirm] = useState<{
    s: StatusEnum;
    message?: string;
    description?: string;
  }>();

  const { trigger: triggerModifyModule } = useSWRMutation(
    `${ServicesURL.modifyModule
      .replace(":corporateId", id || "")
      .replace(":planId", IdPlan || "")
      .replace(":projectId", IdProject || "")
      .replace(":moduleId", IdModule || "")}`,
    (url: string, arg: { arg: any }) => {
      return fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg });
    },
  );

  const onSubmit = async (inputForm: any) => {
    let loParametr = {};

    if (pathAssociation.length > 0) {
      loParametr = {
        ...loParametr,
        ...{
          paths: pathAssociation.map((el: { id: any }) => el.id),
        },
      };
    }
    if (courseAssociation.length > 0) {
      loParametr = {
        ...loParametr,
        ...{
          courses: courseAssociation.map((el: { id: any }) => el.id),
        },
      };
    }
    if (activityAssociation.length > 0) {
      loParametr = {
        ...loParametr,
        ...{
          activities: activityAssociation.map((el: { id: any }) => el.id),
        },
      };
    }
    if (editionAssociation.length > 0) {
      loParametr = {
        ...loParametr,
        ...{
          editions: editionAssociation.map((el: { id: any }) => el.id),
        },
      };
    }

    const objToSend: any = {
      ...inputForm,
      duration: Number(inputForm.duration),
      mandatory_learning_test:
        inputForm.mandatory_learning_test === "true" ? true : false,
      satisfaction_survey:
        inputForm.satisfaction_survey === "true" ? true : false,
      lo_ids: loParametr,
    };

    const { activities, courses, editions, paths, ...resultToSend } = objToSend;
    setOpenToastConfirm(undefined);
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    try {
      await triggerModifyModule(resultToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
      mutate();
    } catch (error) {
      setOpenToast(undefined);
    }
  };

  const onValidation = (inputForm: any) => {
    if (showError) {
      setOpenToast({
        s: StatusEnum.KO,
        message: t("modal-message.error-association") || "",
      });
    } else if (
      activityAssociation.length > 0 ||
      pathAssociation.length > 0 ||
      courseAssociation.length > 0 ||
      editionAssociation.length > 0
    ) {
      setOpenToastConfirm({
        s: StatusEnum.OK,
        message: t("modal-message.attention") || "",
        description: t("modal-message.confirm-association") || "",
      });

      setInputFromValidation(inputForm);
    } else {
      onSubmit(inputForm);
    }
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-badges.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("edit.hero-title")} description={""} />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <AddEditFormCst
              row={dataDetail || []}
              columns={columWithReset || []}
              onSubmit={onValidation}
              actionsForForms={[]}
              getValues={(accessorKey) => getValues(accessorKey)}
              handleAssociation={handleAssociation}
              handleDelete={handleDelete}
              association={openAssociations}
              setOpenAssociation={setOpenAssociation}
              inputsException={inputsException}
              backButton={() => {
                navigate(-1);
              }}
              durationKey={true}
              onReset={() => {
                setActivityAssociation(dataDetail?.lo_ids?.activities || []);
                setCourseAssociation(dataDetail?.lo_ids?.courses || []);
                setPathAssociation(dataDetail?.lo_ids?.paths || []);
                setEditionAssociation(dataDetail?.lo_ids?.editions || []);
              }}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
      <ModalConfirmsCst
        open={!!openToastConfirm}
        title={openToastConfirm?.message || ""}
        description={openToastConfirm?.description || ""}
        onCallBackClosed={() => {
          setOpenToastConfirm(undefined);
        }}
        onActionConfirmed={() => {
          onSubmit(inputFormValidation);
        }}
      />
    </AnimationFadeIn>
  );
};

export default ModuleEdit;
